<template>
  <div class="-transition-panel">
    <div class="-panel-label wt2-text-n2">
      {{ 'transition.SELECT_TRANSITION_IN' | culture }}
      <i
        class="wticon wticon-information1 co-tooltip"
        v-tooltip.extend="selectTransitionInfo"
      />
    </div>

    <div class="-selected-preset-box">
      <animation-item
        :selected="false"
        :anim="selectedTransitionAnim"
        :showTitles="false"
        :autoscroll="false"
        :title="'transition.PANEL_TOOLTIP' | culture"
        @click="$_showPanel"
      />
    </div>

    <div v-if="panelOpen" class="-box-panel">
      <div class="-box-title">
        <div class="-main wt2-text-xl">
          {{ 'transition.PANEL_TITLE' | culture }}
        </div>
        <div class="-sub wt2-text-n">
          {{ 'transition.PANEL_SUBTITLE' | culture }}
        </div>
        <i class="-close wticon wticon-close" @click="$_hidePanel" />
      </div>

      <div class="-box-list">
        <div v-for="(preset, i) of transitionPresets" :key="i" class="preset">
          <div class="preset-title">
            <span class="wt2-text-n" v-text="preset.name" />
          </div>
          <div class="preset-body">
            {{ selectedTransition.wochletName }}

            <animation-item
              v-if="i === 0"
              :selected="selectedTransitionAnim.displayText === 'None'"
              :anim="{ displayText: 'None' }"
              :autoscroll="false"
              @click="setTransition()"
            />

            <animation-item
              v-for="(transition, ii) of preset.transitions"
              :key="ii"
              :showTitles="false"
              :anim="{
                displayText: transition.name
                  ? transition.name
                  : transition.wochletName,
                icon: transition.icon,
              }"
              :selected="
                transition.wochletName === selectedTransition.wiperName &&
                preset.name === selectedTransition.presetName
              "
              :autoscroll="true"
              @click="setTransition(transition, preset)"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex';
import AnimationItem from 'AssetEditor/functionality/Animation/AnimationItem.vue';

export default {
  name: 'TransitionPanel',
  components: {
    'animation-item': AnimationItem,
  },

  data() {
    return {
      panelOpen: false,
      selectedTransition: null,
      selectedByUser: false,
    };
  },

  computed: {
    ...mapGetters('assetEditor', ['assetModel', 'transitionPresets']),

    selectedTransitionPreset() {
      return this.selectedTransition.presetName;
    },

    selectTransitionInfo() {
      return {
        title: app.culture.get('transition.SELECT_TRANSITION_INFO'),
        placement: 'right',
        delay: 250,
      };
    },

    lockTransitionInfo() {
      return {
        title: app.culture.get('transition.LOCK_TRANSITION_INFO'),
        placement: 'bottom',
        delay: 250,
      };
    },

    selectedTransitionAnim() {
      const selected = _.chain(this.transitionPresets)
        .map((p) => p.transitions)
        .flattenDeep()
        .find((x) => x.wochletName === this.selectedTransition.wiperName)
        .tap((x) => x)
        .value();

      if (selected) {
        return {
          displayText: selected.name ? selected.name : selected.wochletName,
          icon: selected.icon,
        };
      } else {
        return { displayText: 'None' };
      }
    },
  },

  created() {
    this.selectedTransition = this.assetModel.getAGD('transition') || {
      wiperName: 'NONE',
    };
    this.$data.selectedByUser = !!this.selectedTransition.selectedByUser;
  },

  methods: {
    ...mapMutations('assetEditor', ['setAGD']),

    $_showPanel() {
      this.$data.panelOpen = true;
    },

    $_hidePanel() {
      this.$data.panelOpen = false;
    },

    setTransition(transition, preset) {
      if (transition) {
        this.selectedTransition = {
          wiperType: 'WIPER',
          wiperName: transition.wochletName,
          presetName: preset.name,
          selectedByUser: this.selectedByUser,
        };
      } else {
        this.selectedTransition = {
          wiperType: null,
          wiperName: null,
          presetName: null,
          selectedByUser: this.selectedByUser,
        };
      }

      this.setAGD({
        transition: this.selectedTransition,
      });

      this.$_hidePanel();
    },
  },
};
</script>

<style lang="scss" scoped>
$wallPadding: 57px;
$scrollableTop: 105px;
$itemWidth: 84px;
$itemScreenHeight: 50px;

.-transition-panel {
  margin-bottom: 30px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 $wallPadding;

  .-panel-label {
    transform: translateY(-10px);
  }

  .-selected-preset-box {
    .-item /deep/ .-screen {
      outline: 1px solid $color-black-l1;
    }
  }

  .-box-panel {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 1;
    width: 100%;
    background-color: white;

    > .-box-title {
      margin: 30px $wallPadding 0;

      > .-main {
      }
      > .-sub {
        color: $color-grey-0;
      }

      > .-close {
        cursor: pointer;
        font-size: 18px;
        position: absolute;
        top: 14px;
        right: 14px;
        padding: 6px;
      }
    }

    > .-box-list {
      position: absolute;
      top: $scrollableTop;
      bottom: 0;
      width: 100%;
      overflow-y: auto;
      overflow-x: hidden;

      .preset {
        transform: translateZ(1px);
        margin: 0 $wallPadding;
        padding-bottom: 10px;

        &:not(:last-child) {
          border-bottom: 1px solid #e5e5e5;
        }

        .preset-title {
          margin-top: 15px;
          margin-bottom: 10px;
        }

        .preset-body {
          display: grid;
          grid-template-columns: auto auto auto auto;
          grid-template-rows: auto;
          column-gap: 10px;
          row-gap: 15px;
          user-select: none;
        }
      }
    }
  }

  .-item {
    width: $itemWidth;
    text-align: center;
    cursor: pointer;
    transition: border-width 0.2s linear;

    &.-selected {
      color: $color-primary;

      /deep/ .-screen {
        border: 4px solid #ffffff;
        outline: 2px solid #06a9f4;
        overflow: hidden;
      }
    }

    /deep/ .-screen {
      width: $itemWidth;
      height: $itemScreenHeight;
      background-color: #f2f2f2;
    }

    /deep/ .-label {
      word-wrap: break-word;
      text-align: center;
      font-size: 12px;
      font-weight: 400;
      line-height: 20px;
    }

    &:hover /deep/ .-label {
      color: $color-normal;
      background: rgba(0, 0, 0, 0.1);
    }
  }
}
</style>
